import type {
  CdcDepositCardSwapInfo,
  CdcDepositCardTopUpInfo,
} from '@cronos-app/binding-swap-sdk/dist/esm/cdc-deposit-card';
import { DEFAULT_USER_CONFIG } from '@cronos-app/swap-sdk';
import { currentWallet } from '@cronos-app/wallet';
import { useQuery } from '@tanstack/react-query';
import { Fraction } from 'bi-fraction';

import { useCardTargetTokens } from '../configs/token';
import type {
  DirectTopUpInfo,
  ExtendedTokenInfo,
  SwapInfo,
} from '../types';
import { useCardLayer } from '../useCardContextStore';
import { getCdcDepositCardService } from './useCdcDepositCardService';
import { useTokenUsdPricesByService } from './useTokenUsdPriceByService';

export const useSwapAndTopUpInfo = (
  from: ExtendedTokenInfo,
  amount: string,
) => {
  const network = currentWallet.useDesiredNetwork();
  const layer = useCardLayer();
  const toTokens = useCardTargetTokens();
  const prices = useTokenUsdPricesByService(toTokens);
  return useQuery({
    queryKey: [
      'useSwapAndTopUpInfo',
      network,
      from,
      amount,
      toTokens,
      layer,
      prices,
    ],
    queryFn: async () => {
      const raw = await getCdcDepositCardService(
        network,
        layer,
      ).getter_swapAndTopUpInfo(
        {
          ...from,
          amount: new Fraction(amount).shl(from.decimals).toFixed(0),
        },
        toTokens,
        prices,
      );
      return pipeSwapAndTopUpInfo(from, toTokens, raw);
    },
    retry: false,
    enabled: +amount > 0,
  });
};

const pipeSwapAndTopUpInfo = (
  fromToken: ExtendedTokenInfo,
  toTokens: ExtendedTokenInfo[],
  info: CdcDepositCardTopUpInfo | CdcDepositCardSwapInfo,
  gasDecimals = 18,
) => {
  const fromAmount = new Fraction(info.from.amount)
    .shr(info.from.decimals)
    .toFixed(info.from.decimals);
  if (!info.needToSwapFirst) {
    const ans: DirectTopUpInfo = {
      ...info,
      fromToken,
      fromAmount: fromAmount,
      toToken: fromToken,
      toAmount: fromAmount,
    };
    return ans;
  }
  const { trade, extra } = info.executionContext;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const pickedTo = toTokens.find(
    (t) => t.address.toLowerCase() === info.to.address.toLowerCase(),
  )!;
  const ans: SwapInfo = {
    ...info,
    fromToken,
    fromAmount,
    toToken: pickedTo,
    toAmount: new Fraction(trade.amountOut.amount)
      .shr(trade.amountOut.decimals)
      .toFixed(trade.amountOut.decimals),
    toMinAmount: new Fraction(extra.receivedMin.amount)
      .shr(extra.receivedMin.decimals)
      .toFixed(extra.receivedMin.decimals),
    paths: trade.routes.map((route) => ({
      percentage: route.percentage,
      path: route.pool
        .map((token) => token.tokenIn.symbol)
        .concat([route.pool[route.pool.length - 1].tokenOut.symbol]),
    })),
    slippageTolerance: DEFAULT_USER_CONFIG.slippageTolerance,
    priceImpact: extra.priceImpact,
    priceImpactExcludeLpFee: extra.priceImpactExcludeLpFee,
    lpFee: new Fraction(extra.lpFee.amount)
      .shr(extra.lpFee.decimals)
      .toFixed(extra.lpFee.decimals),
    gasEstimate: new Fraction(extra.gasEstimate)
      .shr(gasDecimals)
      .toFixed(gasDecimals),
  };
  return ans;
};
